// A2-586 (operator 2026-11-01: "OK") — "clear the earlier answer and decide
// again" on the replay-conflict card. The earlier decision is kept, marked
// withdrawn; the next re-run classifies the row as uploaded and its own
// questions return (api/routes_review_clear_decision). Saved for the one
// re-run, like every Step 4 answer (SavedAnswersBar).
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/authApi";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { CLEAR_DECISION_REASONS } from "../../lib/reviewReasonPresets";

export const ClearDecision = ({ tids = [], allOpen, calcId, calcFrozen, onResolved, label }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const key = allOpen ? "all" : tids.length > 1 ? "ticked" : tids[0];
  if (calcFrozen || (!allOpen && !tids.length)) return null;
  const save = async () => {
    if (busy || reason.trim().length < 8) return;
    setBusy(true); setErr(null);
    try {
      const res = await authClient.post(`/runs/calc/${calcId}/clear-earlier-decision`,
        { ...(allOpen ? { all_open: true } : { txn_ids: tids }), reason: reason.trim() });
      setOpen(false); setReason(""); setPreset("");
      toast.success(t("ui.review_answers.clear_saved", { n: res?.data?.txn_ids?.length ?? 0,
        defaultValue: "Earlier answer cleared on {{n}} row(s) — figures change at the next re-run" }));
      if (onResolved) await onResolved();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setErr(null); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`clear-open-${key}`}
                className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800">
          {label || t("ui.review_answers.clear_open", { defaultValue: "Clear the earlier answer and decide again →" })}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid={`clear-popover-${key}`}
                      className="w-80 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2">
        <p className="text-xs text-slate-700 dark:text-slate-200">
          {t("ui.review_answers.clear_explain", { defaultValue:
            "The earlier answer no longer fits this row. Clearing it keeps it on record; after the next re-run the row asks its own questions again." })}
        </p>
        <PresetReasonSelect testid={`clear-reason-${key}`} presets={CLEAR_DECISION_REASONS} preset={preset}
                            onPick={(p, v) => { setPreset(p); setReason(v); }} />
        {err && <div role="alert" data-testid={`clear-error-${key}`} className="text-xs text-red-700 dark:text-red-300">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setOpen(false)} className="text-xs underline text-slate-600 dark:text-slate-300">
            {t("ui.review_grouped.sc_cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" data-testid={`clear-save-${key}`} disabled={busy || reason.trim().length < 8} onClick={save}
                  aria-busy={busy}
                  className="text-xs font-semibold px-3 py-1 rounded bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white">
            {busy ? t("ui.review_grouped.sc_submitting", { defaultValue: "Saving…" })
                  : t("ui.review_answers.customer_save", { defaultValue: "Save" })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ClearDecision;
